import { useState, useCallback, ReactElement } from 'react';
import { useRoutes, useAwsEffect, useAwsGetter, useAwsBatchIterator } from '@localstack/services';
import { Card, Button } from '@mui/material';

import { Dropdown, ButtonWithDialog, PageTitle, FirehoseStreamsTable } from '@localstack/ui';

import { DEFAULT_FIREHOSE_ROUTES } from '@localstack/constants';

import { FirehoseProps } from './types';

const POLLING_INTERVAL = 5000;

export const FirehoseStreams = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_FIREHOSE_ROUTES,
}: FirehoseProps): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { goto } = useRoutes();

  const { describeDeliveryStream } = useAwsEffect('Firehose', ['describeDeliveryStream'], { clientOverrides });

  const {
    data: deliveryStreamNames,
    isLoading,
    mutate,
  } = useAwsGetter('Firehose', 'listDeliveryStreams', [], {
    swrOverrides: { refreshInterval: POLLING_INTERVAL },
    clientOverrides,
  });

  const deliveryStreams = useAwsBatchIterator(
    deliveryStreamNames?.DeliveryStreamNames,
    async (DeliveryStreamName) => (await describeDeliveryStream({ DeliveryStreamName })).DeliveryStreamDescription,
  );

  const { deleteDeliveryStream } = useAwsEffect('Firehose', ['deleteDeliveryStream'], {
    revalidate: ['listDeliveryStreams', 'describeDeliveryStream'],
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((DeliveryStreamName) => deleteDeliveryStream({ DeliveryStreamName }));
    await Promise.all(promises);
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="Data Firehose: Delivery Streams"
      title={
        <PageTitle
          title="Data Firehose Delivery Streams"
          onMutate={mutate}
          breadcrumbs={[
            ['Firehose', () => goto(routes.RESOURCES_FIREHOSE_STREAMS)],
            ['Delivery Streams', () => goto(routes.RESOURCES_FIREHOSE_STREAMS)],
          ]}
        />
      }
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_FIREHOSE_STREAM_CREATE)}>
            Create Delivery Stream
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Delivery Stream(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Delivery Streams will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <FirehoseStreamsTable
          loading={isLoading}
          deliveryStreams={deliveryStreams}
          onViewStream={(stream) =>
            goto(routes.RESOURCES_FIREHOSE_STREAM, {
              deliveryStreamName: stream.DeliveryStreamName,
            })
          }
          onSelect={setSelectedIds}
        />
      </Card>
    </Layout>
  );
};
